<template>
  <div class="bank-cash">
    <div class="bank-cash__head">
      <div class="bank-cash__title">{{ $t('bank&cash') }}</div>
      <button
        type="button"
        class="bank-cash__all"
        @click="goAll"
      >
        {{ $t('viewAll') }}
        <v-icon x-small>mdi-chevron-left</v-icon>
      </button>
    </div>
    <div
      v-if="canSee"
      class="bank-cash__list"
    >
      <button
        v-for="item in items"
        :key="item.type + '-' + item.id"
        type="button"
        class="bank-cash__row"
        @click="edit(item)"
      >
        <span
          class="bank-cash__icon"
          :class="'is-' + item.type"
        >
          <v-icon size="14">{{ item.type === 'cash' ? 'mdi-cash-register' : 'mdi-bank-outline' }}</v-icon>
        </span>
        <span class="bank-cash__meta">
          <span class="bank-cash__name">{{ item.name }}</span>
          <span class="bank-cash__kind">{{ item.type === 'cash' ? $t('cash') : $t('bank') }}</span>
        </span>
        <span
          class="bank-cash__amount"
          :class="{ 'is-neg': isNeg(item) }"
        >
          {{ $numberFormat(remainOf(item)) }}
          {{ $store.state.currency.name }}
        </span>
      </button>
      <div
        v-if="!loading && !items.length"
        class="bank-cash__empty"
      >
        <home-empty-state :size="80" />
      </div>
      <div
        v-if="loading"
        class="bank-cash__more"
      >
        <v-progress-circular
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
      </div>
    </div>
    <div
      v-else
      class="bank-cash__empty"
    >
      {{ $t("you don't have access to this information") }}
    </div>
    <div
      v-if="canSee && items.length"
      class="bank-cash__foot"
    >
      {{ $t('remain') }}
      {{ $numberFormat(totalRemain) }}
      {{ $store.state.currency.name }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

export default {
  name: 'HomeBankCash',
  components: { HomeEmptyState },
  data () {
    return {
      items: [],
      loading: false
    }
  },
  computed: {
    canSee () {
      return this.$store.getters.getpermission && this.$store.getters.getpermission.includes('overview')
    },
    totalRemain () {
      return this.items.reduce((sum, item) => sum + this.remainOf(item), 0)
    }
  },
  mounted () {
    this.fetchList()
  },
  methods: {
    remainOf (item) {
      return parseFloat(item.remain && item.remain.remain) || 0
    },
    isNeg (item) {
      return this.remainOf(item) < 0
    },
    goAll () {
      this.$router.push({ name: 'bankList' }).catch(() => {})
    },
    edit (item) {
      const name = item.type === 'cash' ? 'cashEdit' : 'bankEdit'
      this.$router.push({ name: name, params: { id: item.id } }).catch(() => {})
    },
    fetchOne (type) {
      return this.$axios.get(this.$endpoint + 'bank', {
        params: {
          type: type,
          page: 1,
          options: { page: 1, itemsPerPage: 8 }
        }
      }).then(res => {
        const rows = (res.data && res.data.data) || []
        return rows.map(row => Object.assign({}, row, { type: type }))
      }).catch(() => [])
    },
    fetchList () {
      this.loading = true
      Promise.all([
        this.fetchOne('bank'),
        this.fetchOne('cash')
      ]).then(groups => {
        const rows = groups[0].concat(groups[1])
        rows.sort((a, b) => Math.abs(this.remainOf(b)) - Math.abs(this.remainOf(a)))
        this.items = rows.slice(0, 8)
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.bank-cash {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px;
  height: 323px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.bank-cash__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.bank-cash__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.bank-cash__all {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  background: none;
  color: #0066FF;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}
.bank-cash__all .v-icon {
  color: #0066FF !important;
}
.bank-cash__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.bank-cash__list::-webkit-scrollbar {
  width: 4px;
}
.bank-cash__list::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 4px;
}
.bank-cash__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border: none;
  border-bottom: 1px solid #F3F4F6;
  background: none;
  cursor: pointer;
  text-align: start;
}
.bank-cash__row:last-child {
  border-bottom: none;
}
.bank-cash__icon {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.bank-cash__icon.is-bank {
  background: #E8F1FF;
}
.bank-cash__icon.is-bank .v-icon {
  color: #0066FF !important;
}
.bank-cash__icon.is-cash {
  background: #E8F8EE;
}
.bank-cash__icon.is-cash .v-icon {
  color: #00A63E !important;
}
.bank-cash__meta {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.bank-cash__name {
  font-size: 12px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bank-cash__kind {
  font-size: 11px;
  color: #9CA3AF;
}
.bank-cash__amount {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: #111;
  white-space: nowrap;
}
.bank-cash__amount.is-neg {
  color: #E7000B;
}
.bank-cash__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
}
.bank-cash__more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
}
.bank-cash__foot {
  flex-shrink: 0;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #E5E7EB;
  font-size: 12px;
  font-weight: 700;
  color: #111;
  text-align: start;
}
</style>
